import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FileText, 
    Clock, 
    CheckCircle, 
    MoreVertical 
} from 'lucide-react';
import { AuthContext } from '../../context/authContext';

// Styles - Ensure these are updated in your content.css
import '../css/dashboard/content.css';
import AdminStatsChart from './chart';
import TargetComparisonChart from './baar';
import AdminStatsChart1 from './chart1';
import DocTypeStatsChart from './chart2';
import AdminStatsChart2 from './chart3';
import UnifiedDashboard from './graph';
import { Datacontext } from '../../context/dataContext';


const StatCard = ({ title, total, processing, completed, icon: Icon, colorClass, navigate, routeMap }) => {
    const [open, setOpen] = useState(false);
    const routes = routeMap[title];

    return (
        <div className={`stat-group-card ${colorClass}`}>
            <div className="card-main-header">
                <div className="header-left">
                    <Icon size={24} />
                    <h3>{title}</h3>
                </div>

                <div className="card-actions-wrapper">
                    <button 
                        className="three-dot-btn" 
                        onClick={() => setOpen(!open)}
                        onBlur={() => setTimeout(() => setOpen(false), 200)}
                    >
                        <MoreVertical size={20} />
                    </button>

                    {open && (
                        <div className="card-dropdown-menu">
                            <div className="dropdown-label">Quick Actions</div>
                            <button onClick={() => navigate(routes.proc.path,{ state: routes.proc.state })}>Go to Processing</button>
                            <button onClick={() => navigate(routes.reg)}>Create New</button>
                            <button onClick={() => navigate(routes.my)}>View My List</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-item">
                    <span className="stat-label">Total</span>
                    <span className="stat-value">{total || 0}</span>
                </div>
                <div className="stat-item processing">
                    <span className="stat-label">Progress</span>
                    <span className="stat-value">{processing || 0}</span>
                </div>
                <div className="stat-item completed">
                    <span className="stat-label">Completed</span>
                    <span className="stat-value">{completed || 0}</span>
                </div>
            </div>
        </div>
    );
};

const DashBoardC = () => {
    const { user } = useContext(AuthContext);
    const { documents, ecs, nls } = useContext(Datacontext);
    const navigate = useNavigate();

    // Route configuration for the 3-dot menu based on card titles

    const routeMap = {
    "Documents": { 
        reg: "/register", 
        my: "/mydoc", 
        proc: { path: "/home", state: { mountSection: 'doc' } } 
    },
    "Encumbrance Certificate": { 
        reg: "/ec_register", 
        my: "/myec", 
        proc: { path: "/home", state: { mountSection: 'ec' } } 
    },
    "Nagal": { 
        reg: "/nl_register", 
        my: "/mynl", 
        proc: { path: "/home", state: { mountSection: 'nl' } } 
    }
};

    const filteredData = useMemo(() => {
        const filterByRole = (data) => {
            if (!data || data.length === 0) return [];
            
            if (user?.role === "user") {
                // Filters for items where user name is in managed_By
                return data.filter(d => 
  d?.managed_By?.map(name => name.toLowerCase()).includes(user.name.toLowerCase()));
            }
            return data;
        };

        return {
            doc: filterByRole(documents),
            ec: filterByRole(ecs),
            nl: filterByRole(nls)
        };
    }, [documents, ecs, nls, user]); // Only re-runs when these change

    // 3. Destructure the memoized results
    const { doc, ec, nl } = filteredData; // Refetch if user context changes

    const calculateStats = (dataArray) => ({
        total: dataArray?.length || 0,
        processing: dataArray?.filter(item => item.docstatus === 'processing')?.length || 0,
        completed: dataArray?.filter(item => item.docstatus === 'completed')?.length || 0
    });

    return (
        <main className="main-area container-xxl">
            <div className="content-padding">
                <header className="page-header">
                    <h1>Dashboard Overview</h1>
                    <p>Welcome back, <strong className='text-uppercase'>{user?.name}</strong></p>
                </header>
        
                <div className="stats-container">
                    <StatCard 
                        title="Documents" 
                        {...calculateStats(doc)}
                        icon={FileText} 
                        colorClass="doc-theme"
                        navigate={navigate}
                        routeMap={routeMap}
                    />
                    <StatCard 
                        title="Encumbrance Certificate" 
                        {...calculateStats(ec)}
                        icon={Clock} 
                        colorClass="ec-theme"
                        navigate={navigate}
                        routeMap={routeMap}
                    />
                    <StatCard 
                        title="Nagal" 
                        {...calculateStats(nl)}
                        icon={CheckCircle} 
                        colorClass="nl-theme"
                        navigate={navigate}
                        routeMap={routeMap}
                    />
                </div>
        
                
                    <div className="admin-sections">
                        <div className="admin-grid-layout">
                            <AdminStatsChart doc={doc} ec={ec} nl={nl} /> 
                            <AdminStatsChart2 doc={doc} ec={ec} nl={nl} />
                        </div>
                    </div>
            
                <UnifiedDashboard doc={doc} ec={ec} nl={nl} />
                <TargetComparisonChart doc={doc} ec={ec} nl={nl}/>
                <div className="admin-sections">
                        <div className="admin-grid-layout">
                            <AdminStatsChart1 doc={doc} ec={ec} nl={nl} /> 
                            <DocTypeStatsChart doc={doc} ec={ec} nl={nl} />
                        </div>
                    </div>
            </div>
        </main>
    );
};

export default DashBoardC;